/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { View } from '@az/starc-ui';
import { useCMSStoreContent } from '../../api/getContentStackStore';
import { CMSLocationsModularHandler } from '../../components/CMSLocationsModularHandler/CMSLocationsModularHandler';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { routePaths } from '@/constants/routePaths';
import {
  type FormattedContentStackStorePage,
  type ContentStackStorePageType,
} from '@/features/contentstack/interface/models';
import { useStoreDetails } from '../../api/getStoreDetails';
import { useLocationsParams } from '../../hooks/useLocationsParams';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useLocationsAnalytics } from '../../hooks/useLocationsAnalytics';
import { LocationsSeoTags } from '../LocationsSeoTags/LocationsSeoTags';
import { useLocationsSEOMetadata } from '../../api/getLocationsSEOMetadata';

type LocationsBaseComponentProps = {
  pageType: ContentStackStorePageType;
};

const placeholderStoreContent: FormattedContentStackStorePage = {
  title: '',
  store_page_body: [
    {
      breadcrumbs: {
        enabled: true,
      },
    },
    {
      store_information: {
        enabled: true,
      },
    },
  ],
};

export function LocationsBaseComponent({ pageType }: LocationsBaseComponentProps) {
  const router = useRouter();

  useLocationsAnalytics();

  const {
    data: storeContent,
    isFetching: storeContentIsFetching,
    isError: storeContentIsError,
    isSuccess: storeContentIsSuccess,
  } = useCMSStoreContent({
    pageType,
    placeholderData: placeholderStoreContent,
  });

  const {
    data: storeDetails,
    isSuccess: storeDetailsIsSuccess,
    isError: storeDetailsIsError,
  } = useStoreDetails();
  const { locationsHome, state, city } = useLocationsParams();

  const { data: seoMetadata } = useLocationsSEOMetadata({
    pageType,
    state,
    city,
  });

  useEffect(() => {
    if (storeContentIsError || storeDetailsIsError) {
      void router.push(routePaths.errorPage);
      return;
    }

    if (storeDetailsIsSuccess && !storeDetails) {
      void router.replace(`/${locationsHome}/${state}/${city}`);
      return;
    }
  }, [
    storeDetailsIsError,
    storeDetailsIsSuccess,
    storeDetails,
    router,
    locationsHome,
    state,
    city,
    storeContentIsError,
  ]);

  return (
    <>
      <LocationsSeoTags
        pageType="store"
        stateName={storeDetails?.address?.region}
        cityName={storeDetails?.address?.city}
        addressLine1={storeDetails?.address?.line1}
        storeNumber={storeDetails?.meta.id}
        coordinates={storeDetails?.yextDisplayCoordinate}
      />
      <View align="center">
        <View maxWidth="1200px" width="100%">
          {storeContentIsFetching ? <ProgressModal noScroll /> : null}
          {storeContentIsSuccess && storeContent.store_page_body ? (
            <CMSLocationsModularHandler
              content={storeContent.store_page_body}
              pageType={pageType}
              seoCopyBlock={seoMetadata?.copy_block}
            />
          ) : null}
        </View>
      </View>
    </>
  );
}
